import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import {
  CinturonesSeguridadCatalogoURL,
  CinturonesSeguridadVehiculoAsignacionURL,
  CinturonesSeguridadVehiculoSyncURL,
} from "../../../API/apiurls";

const emptyAssignment = {
  vehiculoId: null,
  empresaId: null,
  vehiculoNombre: "",
  tipoVehiculo: "",
  cantidadEsperada: 0,
  cantidadAsignada: 0,
  posiciones: [],
  cinturonesSinPosicion: [],
};

const buildPayload = (cinturon, empresaId, vehiculoId, posicionId) => ({
  serialNumero: cinturon.serialNumero,
  nombre: cinturon.nombre,
  asiento: cinturon.asiento,
  activo: cinturon.activo ?? true,
  observacion: cinturon.observacion ?? "",
  empresasModel: { id: Number(empresaId) },
  vehiculosModel: vehiculoId ? { id: Number(vehiculoId) } : null,
  posicionModel: posicionId ? { id: Number(posicionId) } : null,
});

export function VehiculoCinturonesModal({ show, vehiculo, onHide }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignment, setAssignment] = useState(emptyAssignment);
  const [catalogo, setCatalogo] = useState([]);
  const [selectedByPosition, setSelectedByPosition] = useState({});

  const vehiculoId = vehiculo?.id;
  const empresaId = vehiculo?.empresasModel?.id;

  const cargar = async () => {
    if (!show || !vehiculoId) return;
    try {
      setLoading(true);
      const [resAsignacion, resCatalogo] = await Promise.all([
        axios.get(CinturonesSeguridadVehiculoAsignacionURL(vehiculoId)),
        axios.get(CinturonesSeguridadCatalogoURL, {
          params: { empresaId, vehiculoId, activo: true },
        }),
      ]);

      const nextAssignment = resAsignacion.data || emptyAssignment;
      const nextCatalogo = Array.isArray(resCatalogo.data) ? resCatalogo.data : [];
      setAssignment(nextAssignment);
      setCatalogo(nextCatalogo);
      setSelectedByPosition(
        Object.fromEntries(
          (nextAssignment.posiciones || []).map((posicion) => [
            posicion.posicionId,
            posicion.cinturonId ? String(posicion.cinturonId) : "",
          ])
        )
      );
    } catch (error) {
      console.error("Error cargando asignación de cinturones:", error);
      Swal.fire("Error", "No se pudo cargar la asignación del vehículo", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, vehiculoId]);

  const libres = useMemo(() => assignment.cinturonesSinPosicion || [], [assignment.cinturonesSinPosicion]);

  const cinturonesPorPosicion = useMemo(() => {
    return Object.fromEntries(
      (assignment.posiciones || []).map((posicion) => [
        posicion.posicionId,
        [
          ...(posicion.cinturonId
            ? [{
                id: posicion.cinturonId,
                serialNumero: posicion.cinturonSerialNumero,
                nombre: posicion.cinturonNombre,
              }]
            : []),
          ...libres,
        ].filter((item, index, arr) => arr.findIndex((x) => String(x.id) === String(item.id)) === index),
      ])
    );
  }, [assignment.posiciones, libres]);

  const sincronizarPosiciones = async () => {
    if (!vehiculoId) return;
    try {
      setSaving(true);
      await axios.post(CinturonesSeguridadVehiculoSyncURL(vehiculoId));
      await cargar();
      Swal.fire("Listo", "Posiciones sincronizadas", "success");
    } catch (error) {
      console.error("Error sincronizando posiciones:", error);
      Swal.fire("Error", "No se pudieron sincronizar las posiciones", "error");
    } finally {
      setSaving(false);
    }
  };

  const guardarPosicion = async (posicion) => {
    const selectedId = selectedByPosition[posicion.posicionId] || "";
    const actualId = posicion.cinturonId ? String(posicion.cinturonId) : "";
    if (selectedId === actualId) {
      Swal.fire("Sin cambios", "Esa posición ya tiene esa asignación", "info");
      return;
    }

    try {
      setSaving(true);

      if (actualId && actualId !== selectedId) {
        const actual = catalogo.find((item) => String(item.id) === actualId);
        if (actual) {
          await axios.put(`${CinturonesSeguridadCatalogoURL}/${actual.id}`, buildPayload(actual, empresaId, vehiculoId, null));
        }
      }

      if (selectedId) {
        const nuevo = catalogo.find((item) => String(item.id) === selectedId);
        if (!nuevo) {
          throw new Error("Cinturón no encontrado en catálogo");
        }
        await axios.put(`${CinturonesSeguridadCatalogoURL}/${nuevo.id}`, buildPayload(nuevo, empresaId, vehiculoId, posicion.posicionId));
      }

      await cargar();
      Swal.fire("Listo", "Asignación actualizada", "success");
    } catch (error) {
      console.error("Error guardando asignación:", error);
      Swal.fire("Error", "No se pudo guardar la asignación", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="bg-dark text-light border-secondary">
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title>Asignación de cinturones - {vehiculo?.nombre || "Vehículo"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Tipo</div>
            <strong>{vehiculo?.tipoVehiculo || "-"}</strong>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Esperadas</div>
            <strong>{assignment.cantidadEsperada || 0}</strong>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Asignadas</div>
            <strong>{assignment.cantidadAsignada || 0}</strong>
          </div>
          <Button variant="outline-info" onClick={sincronizarPosiciones} disabled={saving || loading}>
            {saving ? "Sincronizando..." : "Sincronizar posiciones"}
          </Button>
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Spinner size="sm" /> Cargando asignación...</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {(assignment.posiciones || []).map((posicion) => (
              <div
                key={posicion.posicionId}
                style={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 12,
                  padding: 14,
                  background: "rgba(255,255,255,0.03)",
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.4fr auto",
                  gap: 12,
                  alignItems: "end",
                }}
              >
                <div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>{posicion.codigo}</div>
                  <strong>{posicion.nombre}</strong>
                </div>

                <Form.Group>
                  <Form.Label style={{ fontSize: 12 }}>Cinturón asignado</Form.Label>
                  <Form.Select
                    value={selectedByPosition[posicion.posicionId] || ""}
                    onChange={(e) => setSelectedByPosition((prev) => ({ ...prev, [posicion.posicionId]: e.target.value }))}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="">Sin asignar</option>
                    {(cinturonesPorPosicion[posicion.posicionId] || []).map((cinturon) => (
                      <option key={cinturon.id} value={String(cinturon.id)}>
                        {cinturon.serialNumero} - {cinturon.nombre || "Sin nombre"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Button variant="primary" onClick={() => guardarPosicion(posicion)} disabled={saving}>
                  Guardar
                </Button>
              </div>
            ))}

            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Cinturones libres del vehículo</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {libres.length ? libres.map((cinturon) => (
                  <span
                    key={cinturon.id}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 999,
                      border: "1px solid rgba(77,163,255,0.24)",
                      background: "rgba(77,163,255,0.14)",
                      fontSize: 12,
                    }}
                  >
                    {cinturon.serialNumero}
                  </span>
                )) : <span style={{ fontSize: 12, opacity: 0.75 }}>No hay cinturones libres</span>}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
}
